/**
 * usePrefetch Hook
 *
 * Unified prefetch hook for any query builder (list or detail).
 */

import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useSessionToken } from "./use-session-token";
import { nextPageOffset } from "./next-page-offset";
import type { QueryBuilder } from "./types";

// ============================================================================
// Types
// ============================================================================

export interface PrefetchOptions {
  /**
   * If true, prefetch for infinite query (appends "infinite" to query key).
   * Use this when the main query uses .getInfinite().
   */
  infinite?: boolean;
}

// ============================================================================
// usePrefetch Hook
// ============================================================================

/**
 * Create a prefetch function for any query builder.
 *
 * Returns a function that can prefetch data for any QueryBuilder
 * (ListQueryBuilder or DetailQueryBuilder).
 *
 * @returns Prefetch function
 *
 * @example
 * ```tsx
 * const prefetch = usePrefetch();
 *
 * // Prefetch list data (single query)
 * const prefetchOrders = () => {
 *   prefetch(useOrdersList().where("status", "eq", "active"));
 * };
 *
 * // Prefetch infinite list data (matches .getInfinite() query key)
 * const prefetchOrdersInfinite = () => {
 *   prefetch(useOrdersList().take(30), { infinite: true });
 * };
 *
 * // Prefetch detail data on hover
 * <Link
 *   to="/places/$placeId"
 *   params={{ placeId: place.id }}
 *   onMouseEnter={() => prefetch(usePlacesDetail(place.id))}
 * >
 *   {place.name}
 * </Link>
 * ```
 */
export function usePrefetch() {
  const queryClient = useQueryClient();
  const getToken = useSessionToken();

  /**
   * Prefetch data for a query builder.
   *
   * @param builder - Any QueryBuilder instance (list or detail)
   * @param options - Prefetch options (e.g., { infinite: true } for infinite queries)
   * @returns Promise that resolves when prefetch is complete
   */
  return useCallback(
    async <T>(
      builder: QueryBuilder<T>,
      options?: PrefetchOptions,
    ): Promise<void> => {
      const token = await getToken();

      if (options?.infinite) {
        // Query key must match what .getInfinite() uses: [...baseKey, "infinite"].
        // The pagination options must ALSO match: prefetchInfiniteQuery replaces
        // the query's options, and when the cache already holds 2+ stale pages
        // it refetches every page — without a pageParam-aware queryFn and the
        // shared getNextPageParam, page 2 throws and errors the mounted list.
        // On an empty/one-page cache this still fetches page one only.
        await queryClient.prefetchInfiniteQuery({
          queryKey: [...builder.getQueryKey(), "infinite"],
          queryFn: ({ pageParam }) => builder.fetch(token, pageParam as number),
          initialPageParam: 0,
          getNextPageParam: nextPageOffset,
        });
      } else {
        await queryClient.prefetchQuery({
          queryKey: builder.getQueryKey(),
          queryFn: () => builder.fetch(token),
        });
      }
    },
    [queryClient, getToken],
  );
}
